import { Trans, useTranslation } from "react-i18next";
import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import camperVan from "../assets/images/camper_van.jpg";
import dericumAxel from "../assets/images/dericum_axel.png";
import felixSchults from "../assets/images/felix_schults.jpg";
import rolandVanDenPol from "../assets/images/roland_van_den_pol.jpg";
import WellerMarkus from "../assets/images/weller_markus.png";
import { Language, toLanguage } from "../type";

type ContactPerson = {
  name: string;
  phone: string;
  email: string;
  image: string;
};

const DEFAULT_CONTACTS: ContactPerson[] = [
  {
    name: "Markus Weller",
    phone: "+49 7733 500 228",
    email: "markus.weller@allsafe-group.com",
    image: WellerMarkus,
  },
];

const CONTACTS_BY_LANGUAGE: Partial<Record<Language, ContactPerson[]>> = {
  [Language.NL]: [
    {
      name: "Roland van den Pol",
      phone: "+31 6 533 858 40",
      email: "roland.vandenpol@allsafe-group.com",
      image: rolandVanDenPol,
    },
    {
      name: "Felix Schults",
      phone: "+31 85 0645 129",
      email: "felix.schults@allsafe-group.com",
      image: felixSchults,
    },
  ],
  [Language.FR]: [
    {
      name: "Axel Dericum",
      phone: "+49 171 561 79 51",
      email: "axel.dericum@allsafe-group.com",
      image: dericumAxel,
    },
  ],
};

const ContactSection = () => {
  const { i18n } = useTranslation();
  const language = toLanguage(i18n.resolvedLanguage);
  const contacts = CONTACTS_BY_LANGUAGE[language] ?? DEFAULT_CONTACTS;

  return (
    <section className="flex xl:max-h-[480px]">
      <div className="hidden lg:block w-1/2 xl:w-1/3">
        <img
          src={camperVan}
          alt="camperVan"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="bg-allsafe-grey px-8 py-8 md:px-8 grow">
        <h2 className="text-allsafe-blue text-4xl leading-10 font-Roboto font-bold pb-2 text-center sm:text-left">
          <Trans i18nKey="contactNow">Jetzt kontaktieren.</Trans>
        </h2>
        <p className="font-Roboto text-center sm:text-left">
          <Trans i18nKey="togetherWeFindSolution">
            Gemeinsam finden wir eine Lösung zur Sicherung Ihrer Ladung.
          </Trans>
        </p>
        <div
          className="flex flex-col space-y-10 items-center sm:items-start md:flex-row md:space-x-24 md:space-y-0 mt-4">
          {contacts.map((person, i) => (
            <div key={i} className="flex flex-col items-center sm:items-start">
              <figure className="w-48">
                <img
                  src={person.image}
                  alt={person.name}
                  className="w-full object-contain scale-x-[-1] ml-8 sm:ml-0"
                />
                <figcaption className="font-Roboto font-light text-center mt-2 sm:text-left">
                  {person.name}
                </figcaption>
              </figure>
              <div className="flex space-x-6 mt-4">
                <div className="w-5 h-5 2xl:w-8 2xl:h-8">
                  <PhoneIcon color="#1D4376"/>
                </div>
                <a
                  className="text-base leading-6 text-allsafe-blue font-Roboto font-light cursor-pointer 2xl:text-2xl 2xl:leading-8"
                  href={`tel:${person.phone.replace(/[^\d+]/g, "")}`}
                >
                  {person.phone}
                </a>
              </div>
              <div className="flex space-x-6 mt-4">
                <div className="w-5 h-5 2xl:w-8 2xl:h-8">
                  <EnvelopeIcon color="#1D4376"/>
                </div>
                <a
                  className="text-base leading-6 text-allsafe-blue font-Roboto font-light cursor-pointer 2xl:text-2xl 2xl:leading-8"
                  href={`mailto:${person.email}`}
                >
                  {person.email}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
